import {
  ACTIVITY_HISTORY_STORE,
  NAVIGATION_SESSION_STORE,
  openDeviceDatabase,
} from "./device-storage";
import type { TrackPoint } from "./offline-draft";
import type { ActivityMarker, NavigationStats } from "./navigation-core";
import type { ActivityKind } from "./thera-data";

export type NavigationSessionState = "recording" | "paused" | "finished";
export type NavigationRecordingMode = "selected-route" | "free";

export type NavigationSession = {
  id: string;
  storageKey: string;
  ownerId: string;
  routeId: string | null;
  routeName: string;
  recordingMode?: NavigationRecordingMode;
  kind: ActivityKind;
  state: NavigationSessionState;
  startedAt: number;
  finishedAt: number | null;
  elapsedSeconds: number;
  points: TrackPoint[];
  markers: ActivityMarker[];
  stats: NavigationStats;
  sourcePlatform: "web" | "android";
  syncStatus: "local" | "pending" | "synced" | "error";
  updatedAt: number;
};

const activeKey = (ownerId: string) => `active-navigation:${ownerId}`;

async function transactionResult<T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
) {
  const database = await openDeviceDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(storeName, mode);
      const request = operation(transaction.objectStore(storeName));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}

export async function saveActiveNavigation(session: NavigationSession) {
  if (!("indexedDB" in window)) return;
  await transactionResult(
    NAVIGATION_SESSION_STORE,
    "readwrite",
    (store) => store.put({ ...session, storageKey: activeKey(session.ownerId), updatedAt: Date.now() }),
  );
}

export async function loadActiveNavigation(ownerId: string) {
  if (!("indexedDB" in window)) return null;
  return await transactionResult<NavigationSession | undefined>(
    NAVIGATION_SESSION_STORE,
    "readonly",
    (store) => store.get(activeKey(ownerId)),
  ) ?? null;
}

export async function clearActiveNavigation(ownerId: string) {
  if (!("indexedDB" in window)) return;
  await transactionResult(NAVIGATION_SESSION_STORE, "readwrite", (store) => store.delete(activeKey(ownerId)));
}

export async function saveActivityHistory(session: NavigationSession) {
  if (!("indexedDB" in window)) return;
  await transactionResult(
    ACTIVITY_HISTORY_STORE,
    "readwrite",
    (store) => store.put({ ...session, state: "finished", updatedAt: Date.now() }),
  );
}

export async function listActivityHistory(ownerId: string, limit = 500) {
  if (!("indexedDB" in window)) return [];
  const database = await openDeviceDatabase();
  try {
    const items = await new Promise<NavigationSession[]>((resolve, reject) => {
      const index = database.transaction(ACTIVITY_HISTORY_STORE, "readonly")
        .objectStore(ACTIVITY_HISTORY_STORE)
        .index("ownerId");
      const request = index.getAll(ownerId);
      request.onsuccess = () => resolve(request.result as NavigationSession[]);
      request.onerror = () => reject(request.error);
    });
    return items.sort((a, b) => b.startedAt - a.startedAt).slice(0, limit);
  } finally {
    database.close();
  }
}

export async function listPendingActivitySyncBatch(
  ownerId: string,
  limit = 25,
  includeErrors = false,
): Promise<NavigationSession[]> {
  if (!("indexedDB" in window) || limit <= 0) return [];
  const database = await openDeviceDatabase();
  try {
    return await new Promise<NavigationSession[]>((resolve, reject) => {
      const transaction = database.transaction(ACTIVITY_HISTORY_STORE, "readonly");
      const index = transaction.objectStore(ACTIVITY_HISTORY_STORE).index("ownerId");
      const request = index.openCursor(IDBKeyRange.only(ownerId));
      const items: NavigationSession[] = [];

      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor || items.length >= limit) {
          resolve(items);
          return;
        }

        const activity = cursor.value as NavigationSession;
        const retryable = activity.syncStatus !== "synced"
          && (includeErrors || activity.syncStatus !== "error");
        if (retryable) items.push(activity);
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  } finally {
    database.close();
  }
}
export async function updateActivitySyncStatus(
  id: string,
  syncStatus: NavigationSession["syncStatus"],
) {
  if (!("indexedDB" in window)) return;
  const database = await openDeviceDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(ACTIVITY_HISTORY_STORE, "readwrite");
      const store = transaction.objectStore(ACTIVITY_HISTORY_STORE);
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) store.put({ ...request.result, syncStatus, updatedAt: Date.now() });
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally {
    database.close();
  }
}
